import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Modal } from "@dropbox/dig-components/modal";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { getDefaultPromptId } from "@mirage/service-compute-relevance";
import type {
  PreviousPrompt,
  RelevanceRubric,
} from "@mirage/service-compute-relevance/service/types";
import { PROMPT_TYPE } from "@mirage/service-compute-relevance/service/types";
import { showSnackbar } from "@mirage/shared/snackbar";
import { getTimeAgoString } from "@mirage/shared/util/time";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { useEffect, useState } from "react";
import styles from "./RecentPromptsModule.module.css";
import { useRecentPrompts } from "./useRecentPrompts";

const MAX_PROMPTS_TO_SHOW = 5;

export const RecentPromptsModule = ({
  onSelectPrompt,
  rubric,
}: {
  onSelectPrompt: (prompt: string) => void;
  rubric: RelevanceRubric;
}) => {
  const { recentPrompts, selectedPromptId, setSelectedPromptId, deletePrompt } =
    useRecentPrompts(rubric);
  const [deletePromptId, setDeletePromptId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userDefaultPromptId, setUserDefaultPromptId] = useState<string | null>(
    null,
  );

  // Load the current default prompt ID when the component mounts
  useEffect(() => {
    getDefaultPromptId(rubric)
      .then(setUserDefaultPromptId)
      .catch(() => {
        // Ignore error, just keep it as null
      });
  }, [recentPrompts]);

  const handleSelectItem = (prompt: PreviousPrompt) => {
    if (prompt) {
      onSelectPrompt(prompt.prompt);
      setSelectedPromptId(prompt.uuid);
    }
  };

  const handleDeleteClick = (
    promptId: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    // Stop propagation to prevent selecting the prompt when clicking delete
    event.stopPropagation();

    // Don't allow deletion of default or backend prompts
    const promptToDelete = recentPrompts.find((p) => p.uuid === promptId);

    if (
      promptToDelete?.type === PROMPT_TYPE.FRONTEND ||
      promptToDelete?.type === PROMPT_TYPE.BACKEND
    ) {
      return;
    }

    setDeletePromptId(promptId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletePromptId) return;

    try {
      await deletePrompt(deletePromptId);

      setIsDeleteModalOpen(false);
      setDeletePromptId(null);

      if (selectedPromptId === deletePromptId) {
        // Get the default prompt if the deleted prompt was the selected prompt
        const frontendPrompt = recentPrompts.find(
          (p) => p.type === PROMPT_TYPE.FRONTEND,
        );

        setSelectedPromptId(frontendPrompt?.uuid || null);
        onSelectPrompt(frontendPrompt?.prompt || "");
      }

      showSnackbar({
        title: i18n.t("delete_search_relevance_prompt"),
      });
    } catch (error) {
      showSnackbar({
        title: i18n.t("delete_search_relevance_prompt_error"),
      });
      setIsDeleteModalOpen(false);
      setDeletePromptId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletePromptId(null);
  };

  if (!recentPrompts.length) {
    return null;
  }

  // Find the prompt being deleted (if any)
  const promptToDelete = deletePromptId
    ? recentPrompts.find((p) => p.uuid === deletePromptId)
    : null;

  return (
    <div>
      <Text className={styles.title}>
        <strong>{i18n.t("search_relevance_recent_prompts")}</strong>
      </Text>

      {recentPrompts.slice(0, MAX_PROMPTS_TO_SHOW).map((prompt) => {
        const isUserDefault = userDefaultPromptId === prompt.uuid;

        return (
          <div
            key={prompt.uuid}
            className={classNames(styles.container, {
              [styles.selected]:
                selectedPromptId === prompt.uuid ||
                (selectedPromptId === null &&
                  // Backend prompt is the default
                  prompt.type === PROMPT_TYPE.BACKEND),
              [styles.defaultPrompt]: isUserDefault,
            })}
            onClick={() => handleSelectItem(prompt)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSelectItem(prompt);
              }
            }}
            tabIndex={0}
            role="button"
          >
            <div>
              <div>
                <Text>
                  {prompt.type === PROMPT_TYPE.BACKEND
                    ? i18n.t("backend_prompt")
                    : prompt.type === PROMPT_TYPE.FRONTEND
                      ? i18n.t("frontend_prompt")
                      : i18n.t("customized_prompt")}
                </Text>
              </div>
              {prompt.type == PROMPT_TYPE.CUSTOM && (
                <div>
                  <Text size="small" color="faint">
                    {getTimeAgoString(prompt.time)}
                  </Text>
                </div>
              )}
            </div>
            <div className={styles.deleteButton}>
              {prompt.type !== PROMPT_TYPE.FRONTEND &&
                prompt.type !== PROMPT_TYPE.BACKEND && (
                  <IconButton
                    variant="transparent"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                      handleDeleteClick(prompt.uuid, e)
                    }
                    aria-label={i18n.t("delete_search_relevance_prompt_title")}
                  >
                    <UIIcon src={CloseLine} />
                  </IconButton>
                )}
            </div>
          </div>
        );
      })}

      {/* Inline Confirmation Modal */}
      {promptToDelete && (
        <Modal
          open={isDeleteModalOpen}
          withCloseButton="Close"
          onRequestClose={handleCancelDelete}
        >
          <Modal.Header hasBottomSpacing="title-standard">
            <Modal.Title>
              {i18n.t("delete_search_relevance_prompt_title")}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body hasVerticalSpacing={true}>
            {i18n.t("delete_search_relevance_prompt_body")}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="opacity" onClick={handleCancelDelete}>
              {i18n.t("cancel")}
            </Button>
            <Button
              variant="primary"
              tone="destructive"
              onClick={handleConfirmDelete}
            >
              {i18n.t("delete_search_relevance_prompt_title")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
